import '@apps/web/routes/_app/admin/agent/theme.css';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, ChartNoAxesCombined, Sparkles } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    AdminAnalyticsChatRail,
    type AdminAnalyticsDataSource,
} from '@apps/web/features/admin_analytics/admin_analytics_chat_rail';
import {
    captureAdminPageIds,
    findOnlyCreatedAdminPage,
} from '@apps/web/features/admin_analytics/admin_analytics_run_changes';
import { AdminAnalyticsWorkspaceLayout } from '@apps/web/features/admin_analytics/admin_analytics_workspace_layout';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

export const Route = createFileRoute('/_app/admin/analytics/')({
    component: AdminAnalyticsIndex,
    head: () => ({
        meta: [{ title: 'Analytics | Phoebe Admin' }],
    }),
});

function AdminAnalyticsIndex() {
    const navigate = useNavigate();
    const { data, error, isLoading, mutate } = useApiQuery({
        path: '/admin/agent/pages',
        method: 'get',
        select: (response) => response,
    });
    const pageIdsAtRunStart = useRef<ReadonlySet<string> | null>(null);
    const [dataSource, setDataSource] =
        useState<AdminAnalyticsDataSource>('postgres');

    const handleRunStarted = () => {
        pageIdsAtRunStart.current = captureAdminPageIds(data?.pages);
    };

    const handleRunSettled = async () => {
        const previousPageIds = pageIdsAtRunStart.current;
        pageIdsAtRunStart.current = null;
        const refreshed = await mutate();
        const created = findOnlyCreatedAdminPage(
            previousPageIds,
            refreshed?.pages,
        );
        if (created) {
            await navigate({
                to: '/admin/analytics/$slug',
                params: { slug: created.slug },
            });
        }
    };

    const canvas = (
        <main className="bg-background h-full min-h-0 overflow-auto p-5 md:p-8">
            <div className="mx-auto flex max-w-6xl flex-col gap-6">
                <header className="border-b pb-5">
                    <p className="aa-eyebrow">Analytics workspace</p>
                    <h1 className="mt-1 text-2xl font-semibold">
                        Build dashboards with Admin Agent
                    </h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-6">
                        Ask in the agent rail to create or revise a dashboard.
                        Each dashboard owns its organization, date, and other
                        controls. Queries run server-side against the source
                        selected below.
                    </p>
                    <div className="mt-4 flex flex-wrap items-end gap-3">
                        <label className="grid gap-1.5 text-sm font-medium">
                            Data source for new dashboard
                            <Select
                                value={dataSource}
                                onValueChange={(value) =>
                                    setDataSource(
                                        value as AdminAnalyticsDataSource,
                                    )
                                }
                            >
                                <SelectTrigger
                                    className="w-56"
                                    aria-label="Data source for new dashboard"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="postgres">
                                        Phoebe database
                                    </SelectItem>
                                    <SelectItem value="snowflake">
                                        Snowflake
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </label>
                        <p className="text-muted-foreground max-w-lg pb-1 text-xs leading-5">
                            {dataSource === 'snowflake'
                                ? 'Uses the governed Snowflake analytics warehouse and its exported history.'
                                : 'Uses the production analytics read replica for current operational data.'}
                        </p>
                    </div>
                </header>
                {isLoading ? (
                    <CanvasState>Loading dashboards…</CanvasState>
                ) : error ? (
                    <CanvasState>Dashboards could not be loaded.</CanvasState>
                ) : data?.pages.length ? (
                    <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {data.pages.map((page) => (
                            <li key={page.id}>
                                <Link
                                    to="/admin/analytics/$slug"
                                    params={{ slug: page.slug }}
                                    className="aa-card group flex h-full min-h-36 flex-col p-4 transition-colors hover:border-[var(--aa-border-strong)]"
                                >
                                    <div className="flex items-start gap-3">
                                        <ChartNoAxesCombined className="aa-ink-muted mt-0.5 size-5 shrink-0" />
                                        <span className="aa-title min-w-0 flex-1 truncate">
                                            {page.title}
                                        </span>
                                        <ArrowRight className="aa-ink-faint size-4 transition-transform group-hover:translate-x-0.5" />
                                    </div>
                                    <p className="aa-meta mt-2 truncate">
                                        /{page.slug}
                                    </p>
                                    <p className="aa-caption mt-auto pt-6">
                                        {page.active_version_id
                                            ? 'Published'
                                            : 'Draft only'}
                                    </p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <CanvasState>
                        <Sparkles className="mx-auto mb-3 size-5" />
                        Start a chat and describe the dashboard you want.
                    </CanvasState>
                )}
            </div>
        </main>
    );

    return (
        <div className="admin-agent-page h-full min-h-[calc(100svh-3.5rem)] lg:min-h-svh">
            <AdminAnalyticsWorkspaceLayout
                canvas={canvas}
                agent={
                    <AdminAnalyticsChatRail
                        pageSlug={null}
                        pageTitle={null}
                        dataSource={dataSource}
                        onRunStarted={handleRunStarted}
                        onRunSettled={handleRunSettled}
                    />
                }
                details={
                    <div className="space-y-4 p-5 text-sm">
                        <h2 className="font-semibold">How it works</h2>
                        <p className="text-muted-foreground leading-6">
                            Admin Agent authors a data-free presentation and an
                            immutable parameterized query package. The server
                            validates and executes it against the dashboard’s
                            saved data source, then saves a refreshable
                            snapshot.
                        </p>
                        <div className="rounded-md border p-3 text-xs leading-5">
                            There is no workspace-wide organization selector.
                            Ask the agent to add one to a dashboard when the
                            analysis needs it.
                        </div>
                    </div>
                }
            />
        </div>
    );
}

function CanvasState({ children }: { readonly children: ReactNode }) {
    return (
        <div className="text-muted-foreground rounded-md border border-dashed p-12 text-center text-sm">
            {children}
        </div>
    );
}
